import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import {
    Notification,
    useVisible,
    Button,
    Box,
    useBreakpoint,
    zIndex,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { setCookie, getCookie } from 'helpers/cookie';
import { CONFIG } from 'utils/config';
import { useGetIsGDPR } from 'hooks/useGetIsGDPR';

const CookieConsentWrapper = styled.div`
    position: fixed;
    bottom: 6rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: ${zIndex('subOverlay')};
    button {
        white-space: nowrap;
    }
    span {
        white-space: normal;
    }
    .notification {
        max-width: calc(100vw - 2rem);
        width: max-content;
        height: auto;
    }
`;

const handleOpenPrivacyPage = () => {
    window.open(`${CONFIG.VITE_GUESTSITE_URL}/en/privacy/`, '_blank');
};

const CookieConsent = (): JSX.Element | null => {
    const { show, hide, visible } = useVisible();
    const [consented, setConsented] = useState(false);
    const isMobile = useBreakpoint('md');
    const requiresConsent = useGetIsGDPR();

    const accept = useCallback(() => {
        setCookie('cookieconsent_status', 'dismiss', {
            path: '/',
            domain: CONFIG.VITE_COOKIE_DOMAIN,
            expirationDays: 365,
        });
        hide();
    }, [hide]);

    useEffect(() => {
        if (!consented) {
            const userConsent = getCookie('cookieconsent_status') === 'dismiss';

            if (userConsent) {
                setConsented(true);
            } else if (requiresConsent === true) {
                show();
            }
        }
    }, [consented, setConsented, show, requiresConsent]);

    return visible ? (
        <CookieConsentWrapper>
            <Notification
                variant="success"
                kind="persistent"
                cta={t`I agree`}
                onClose={accept}
                className={'notification'}
            >
                <Box
                    display="flex"
                    flexDirection={isMobile ? 'column' : 'row'}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                >
                    <Trans>
                        By using our website, you agree to the use of cookies as
                        described in our
                        <Button
                            as="a"
                            onClick={handleOpenPrivacyPage}
                            kind="link"
                            variant="subtle"
                            pr={0}
                            pl={isMobile ? 0 : 'xxs'}
                        >
                            Privacy Policy
                        </Button>
                    </Trans>
                </Box>
            </Notification>
        </CookieConsentWrapper>
    ) : null;
};

export default CookieConsent;
